<template>
  <div class="app-page near-persons-page">
    <div class="near-content">
      <!-- 谁看过我区域 -->
      <section class="near-section">
        <div class="section-header">
          <div class="section-header-left">
            <div class="section-icon-wrapper">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </div>
            <div>
              <h3 class="section-title">谁看过我</h3>
              <p class="section-desc">谁曾悄悄访问过你的主页</p>
            </div>
          </div>
          <button
            class="section-refresh-btn"
            :disabled="loadingViewed"
            @click="fetchViewedUsers"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'spinning': loadingViewed }"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
            刷新
          </button>
        </div>

        <div v-if="loadingViewed" class="skeleton-grid">
          <div v-for="n in 4" :key="n" class="skeleton-card">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-lines">
              <div class="skeleton-line w-60"></div>
              <div class="skeleton-line w-40"></div>
              <div class="skeleton-line w-80"></div>
            </div>
            <div class="skeleton-actions">
              <div class="skeleton-btn"></div>
              <div class="skeleton-btn"></div>
            </div>
          </div>
        </div>

        <div v-else-if="viewedUsers.length > 0" class="user-grid">
          <div
            v-for="user in viewedUsers"
            :key="user.userId"
            class="user-card"
          >
            <div class="user-card-bg"></div>
            <div class="user-card-top">
              <img
                :src="user.avatar || 'https://picsum.photos/id/64/200'"
                :alt="user.nickname"
                class="user-avatar"
              />
              <div class="user-view-time">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {{ formatTime(user.viewTime) }}
              </div>
            </div>
            <div class="user-card-body">
              <h4 class="user-nickname">{{ user.nickname }}</h4>
              <p class="user-basic">{{ user.age }}岁 · {{ user.gender === 1 ? '男' : '女' }}</p>
              <p class="user-hobby">{{ user.hobby || '暂无爱好' }}</p>
            </div>
            <div class="user-card-actions">
              <button class="action-btn action-primary" @click="matchUser(user.userId)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                发起匹配
              </button>
              <button class="action-btn action-secondary" @click="viewUserDetail(user.userId)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                详情
              </button>
            </div>
          </div>
        </div>

        <div v-else class="empty-section">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#B2BEC3" stroke-width="1.2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          <p>暂无用户查看过你</p>
          <span>完善个人资料，吸引更多人关注</span>
        </div>
      </section>

      <!-- 附近的人区域 -->
      <section class="near-section">
        <div class="section-header">
          <div class="section-header-left">
            <div class="section-icon-wrapper icon-location">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </div>
            <div>
              <h3 class="section-title">附近的人</h3>
              <p class="section-desc">地理距离相近的潜在好友</p>
            </div>
          </div>
          <button
            class="section-refresh-btn"
            :disabled="loadingNearby"
            @click="updateLocationAndGetNearby"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'spinning': loadingNearby }"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 21 13 21 10a9 9 0 10-18 0c0 3 3.7 7 9 11.7z"/></svg>
            定位并刷新
          </button>
        </div>

        <div v-if="loadingNearby" class="skeleton-grid">
          <div v-for="n in 4" :key="n" class="skeleton-card">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-lines">
              <div class="skeleton-line w-60"></div>
              <div class="skeleton-line w-40"></div>
              <div class="skeleton-line w-80"></div>
            </div>
            <div class="skeleton-actions">
              <div class="skeleton-btn"></div>
              <div class="skeleton-btn"></div>
            </div>
          </div>
        </div>

        <div v-else-if="nearbyUsers.length > 0" class="user-grid">
          <div
            v-for="user in nearbyUsers"
            :key="user.userId"
            class="user-card"
          >
            <div class="user-card-bg bg-location"></div>
            <div class="user-card-top">
              <img
                :src="user.avatar || 'https://picsum.photos/id/64/200'"
                :alt="user.nickname"
                class="user-avatar"
              />
              <div class="user-distance">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 21 13 21 10a9 9 0 10-18 0c0 3 3.7 7 9 11.7z"/></svg>
                {{ user.distance ? (user.distance >= 1000 ? (user.distance / 1000).toFixed(1) + 'km' : user.distance + 'm') : '未知距离' }}
              </div>
            </div>
            <div class="user-card-body">
              <h4 class="user-nickname">{{ user.nickname }}</h4>
              <p class="user-basic">{{ user.age }}岁 · {{ user.gender === 1 ? '男' : '女' }}</p>
              <p class="user-hobby">{{ user.hobby || '暂无爱好' }}</p>
            </div>
            <div class="user-card-actions">
              <button class="action-btn action-primary" @click="matchUser(user.userId)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                发起匹配
              </button>
              <button class="action-btn action-secondary" @click="viewUserDetail(user.userId)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                详情
              </button>
            </div>
          </div>
        </div>

        <div v-else class="empty-section">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#B2BEC3" stroke-width="1.2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          <p>暂无附近用户</p>
          <span>开启定位，发现身边的人</span>
        </div>
      </section>
    </div>

    <!-- 用户详情弹窗 -->
    <el-dialog
      v-model="showDetailDialog"
      title="用户详情"
      width="500px"
      :close-on-click-modal="false"
      class="detail-dialog"
    >
      <div v-if="userDetailLoading" class="skeleton-container">
        <el-skeleton :rows="6" animated />
      </div>
      <div v-else class="detail-content">
        <div class="detail-avatar">
          <img :src="userDetail.avatar || 'https://picsum.photos/id/64/200'" alt="头像" />
          <div class="detail-avatar-ring"></div>
        </div>
        <el-descriptions :column="1" class="detail-descriptions">
          <el-descriptions-item label="昵称">{{ userDetail.nickname }}</el-descriptions-item>
          <el-descriptions-item label="年龄">{{ userDetail.age }}岁</el-descriptions-item>
          <el-descriptions-item label="性别">{{ userDetail.gender === 1 ? '男' : '女' }}</el-descriptions-item>
          <el-descriptions-item label="爱好">{{ userDetail.hobby || '暂无爱好' }}</el-descriptions-item>
          <el-descriptions-item label="距离">{{ currentUserDistance }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import matchApi from '@/api/match.js'
import axios from 'axios'

// 响应式数据
const userId = ref(localStorage.getItem('userId') || null)
const viewedUsers = ref([])
const nearbyUsers = ref([])
const loadingViewed = ref(false)
const loadingNearby = ref(false)

// 查看详情需要的变量
const userDetail = ref({})
const currentUserDistance = ref('')
const showDetailDialog = ref(false)
const userDetailLoading = ref(false)

// 格式化时间
const formatTime = (time) => {
  if (!time) return ''
  const date = new Date(time)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 获取谁看过我
const fetchViewedUsers = async () => {
  if (!userId.value) return

  loadingViewed.value = true
  try {
    const res = await matchApi.getViewedMe(userId.value)
    if (res.data && (res.data.code === 200 || res.data.success)) {
      viewedUsers.value = res.data.data?.records || res.data.records || []
    }
  } catch (error) {
    ElMessage.error('获取查看记录失败')
  } finally {
    loadingViewed.value = false
  }
}

// 获取附近的人
const getNearbyUsers = async () => {
  if (!userId.value) return

  loadingNearby.value = true
  try {
    const res = await matchApi.getNearbyPeople(userId.value)
    if (res.data && (res.data.code === 200 || res.data.success)) {
      nearbyUsers.value = res.data.data || res.data.records || []
    }
  } catch (error) {
    ElMessage.error('获取附近用户失败')
  } finally {
    loadingNearby.value = false
  }
}

// 更新位置并获取附近的人
const updateLocationAndGetNearby = async () => {
  if (!userId.value) return

  loadingNearby.value = true
  try {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords
        console.log('当前用户的位置为'+latitude+','+longitude)
        await matchApi.updateLocation({
          userId: userId.value,
          latitude: latitude,
          longitude: longitude
        })
        await getNearbyUsers()
        ElMessage.success('位置更新成功，已获取附近用户')
      }, () => {
        ElMessage.error('获取位置失败，请手动刷新')
        getNearbyUsers()
      })
    } else {
      ElMessage.error('浏览器不支持地理定位')
      getNearbyUsers()
    }
  } catch (error) {
    ElMessage.error('操作失败，请重试')
  } finally {
    loadingNearby.value = false
  }
}

// 匹配用户
const matchUser = async (targetUserId) => {
  if (!userId.value) return

  try {
    const res = await matchApi.likeUser({
      userId: userId.value,
      targetUserId: targetUserId
    })
    if (res.data.success) {
      ElMessage.success('已向该用户发起匹配')
      nearbyUsers.value = nearbyUsers.value.filter(user => user.userId !== targetUserId)
      viewedUsers.value = viewedUsers.value.filter(user => user.userId !== targetUserId)
    }
  } catch (error) {
    ElMessage.error('匹配失败，请稍后重试')
  }
}

// 查看用户详情
const viewUserDetail = async (targetUserId) => {
  if (!targetUserId) return

  userDetailLoading.value = true
  userDetail.value = {}
  currentUserDistance.value = ''

  try {
    const response = await axios.get('/user-service/api/v1/users/getById', {
      params: { id: targetUserId }
    })

    if (response.data.code == 200) {
      userDetail.value = response.data.data

      const user = nearbyUsers.value.find(u => u.userId === targetUserId)
                    || viewedUsers.value.find(u => u.userId === targetUserId)
      if (user?.distance) {
        currentUserDistance.value = user.distance + '米'
      } else {
        currentUserDistance.value = '未知'
      }

      showDetailDialog.value = true
    } else {
      ElMessage.error('获取用户详情失败')
    }
  } catch (error) {
    console.error('获取用户详情失败:', error)
    ElMessage.error('获取用户详情失败，请稍后重试')
  } finally {
    userDetailLoading.value = false
  }
}

// 初始化数据
onMounted(() => {
  if (userId.value) {
    fetchViewedUsers()
    updateLocationAndGetNearby()
  }
})
</script>

<style scoped>
.near-persons-page {
  min-height: 100%;
}

.near-content {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── 区块 ── */
.near-section {
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-lg);
  box-shadow: var(--app-shadow-card);
  border: 1px solid var(--app-border-subtle);
  padding: 24px;
  transition: box-shadow 0.25s ease;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--app-border-light);
}

.section-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.section-icon-wrapper {
  width: 42px;
  height: 42px;
  border-radius: var(--app-radius-md);
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-icon-wrapper.icon-location {
  background: rgba(108, 92, 231, 0.1);
  color: #8B7CF7;
}

.section-title {
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0;
}

.section-desc {
  font-size: 0.8125rem;
  color: var(--app-text-muted);
  margin: 2px 0 0;
}

.section-refresh-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid var(--app-border-light);
  border-radius: var(--app-radius-full);
  background: var(--app-bg-elevated);
  color: var(--app-text-secondary);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.section-refresh-btn:hover {
  border-color: var(--app-color-primary);
  color: var(--app-color-primary);
  background: var(--app-color-primary-soft);
}

.section-refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.spinning {
  animation: spin 1s linear infinite;
}

/* ── 骨架屏 ── */
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
}

.skeleton-card {
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-md);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.skeleton-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-lines {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.skeleton-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.w-60 { width: 60%; }
.w-40 { width: 40%; }
.w-80 { width: 80%; }

.skeleton-actions {
  display: flex;
  gap: 10px;
  width: 100%;
}

.skeleton-btn {
  flex: 1;
  height: 36px;
  border-radius: 18px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* ── 卡片网格 ── */
.user-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
}

/* ── 用户卡片 ── */
.user-card {
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-md);
  border: 1px solid var(--app-border-light);
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  animation: cardIn 0.4s ease both;
}

.user-card:nth-child(1) { animation-delay: 0s; }
.user-card:nth-child(2) { animation-delay: 0.05s; }
.user-card:nth-child(3) { animation-delay: 0.1s; }
.user-card:nth-child(4) { animation-delay: 0.15s; }

@keyframes cardIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.user-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--app-shadow-card-hover);
  border-color: transparent;
}

.user-card-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: var(--app-gradient-primary);
  opacity: 0.08;
}

.user-card-bg.bg-location {
  background: linear-gradient(135deg, #8B7CF7 0%, #A99BFF 100%);
  opacity: 0.1;
}

.user-card-top {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px 12px;
  position: relative;
}

.user-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid var(--app-bg-elevated);
  box-shadow: 0 4px 12px rgba(255, 126, 126, 0.2);
  position: relative;
  z-index: 1;
  transition: transform 0.3s;
}

.user-card:hover .user-avatar {
  transform: scale(1.05);
}

.user-view-time,
.user-distance {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: var(--app-text-secondary);
  margin-top: 8px;
  background: var(--app-bg-soft);
  padding: 4px 12px;
  border-radius: var(--app-radius-full);
}

.user-distance {
  color: #8B7CF7;
  background: rgba(108, 92, 231, 0.08);
}

.user-card-body {
  padding: 0 16px 12px;
  text-align: center;
}

.user-nickname {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0 0 4px;
}

.user-basic {
  font-size: 0.8125rem;
  color: var(--app-text-secondary);
  margin: 0 0 4px;
}

.user-hobby {
  font-size: 0.75rem;
  color: var(--app-text-muted);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── 卡片操作按钮 ── */
.user-card-actions {
  display: flex;
  gap: 8px;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--app-border-light);
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px;
  border-radius: var(--app-radius-full);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  border: none;
}

.action-primary {
  background: var(--app-gradient-primary);
  color: white;
  box-shadow: 0 2px 8px rgba(255, 126, 126, 0.2);
}

.action-primary:hover {
  box-shadow: 0 4px 14px rgba(255, 126, 126, 0.35);
  transform: translateY(-1px);
}

.action-secondary {
  background: var(--app-bg-soft);
  color: var(--app-text-secondary);
  border: 1px solid var(--app-border-light);
}

.action-secondary:hover {
  border-color: var(--app-color-primary);
  color: var(--app-color-primary);
  background: var(--app-color-primary-soft);
}

/* ── 空状态 ── */
.empty-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: var(--app-text-muted);
  text-align: center;
}

.empty-section svg {
  margin-bottom: 12px;
}

.empty-section p {
  font-size: 0.9375rem;
  color: var(--app-text-secondary);
  margin: 0 0 4px;
  font-weight: 500;
}

.empty-section span {
  font-size: 0.8125rem;
  color: var(--app-text-muted);
}

/* ── 详情弹窗 ── */
.detail-dialog :deep(.el-dialog__body) {
  padding-top: 8px;
}

.detail-content {
  text-align: center;
  padding: 8px 0;
}

.detail-avatar {
  position: relative;
  display: inline-block;
  margin-bottom: 24px;
}

.detail-avatar img {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  position: relative;
  z-index: 1;
}

.detail-avatar-ring {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  border: 3px solid var(--app-color-primary-soft);
  z-index: 0;
}

.detail-descriptions :deep(.el-descriptions__label) {
  color: var(--app-text-secondary);
  font-weight: 500;
}

.skeleton-container {
  padding: 20px 0;
}

/* ── 响应式 ── */
@media (max-width: 768px) {
  .near-section {
    padding: 16px;
    border-radius: var(--app-radius-md);
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .section-refresh-btn {
    align-self: flex-start;
  }

  .user-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
  }

  .user-avatar {
    width: 64px;
    height: 64px;
  }

  .user-card-actions {
    flex-direction: column;
  }

  .action-btn {
    padding: 6px 10px;
    font-size: 0.75rem;
  }
}

@media (max-width: 400px) {
  .user-grid {
    grid-template-columns: 1fr;
  }
}
</style>